import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { campaigns as api, type Campaign } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Spinner from "@/components/Spinner";
import InboxPreviewPanel from "@/components/InboxPreviewPanel";
import OptimizationPanel from "@/components/OptimizationPanel";
import {
  Plus, Send, Trash2, X, ChevronDown, Sparkles, Eye, TrendingUp,
  Zap, MoreVertical, Copy, Mail, Search, Download, UserPlus, Pause, Play, FileText,
} from "lucide-react";

const STATUS_FILTERS = ["", "draft", "scheduled", "sending", "sent", "throttled", "paused"] as const;

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [items, setItems]           = useState<Campaign[]>([]);
  const [nextToken, setNextToken]   = useState<string | null>(null);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter]         = useState("");
  const [search, setSearch]         = useState("");

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName]       = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [creating, setCreating]     = useState(false);

  // Action state
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);
  const [optimizeCampaign, setOptimizeCampaign] = useState<Campaign | null>(null);
  const [menuOpen, setMenuOpen]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.list({ status: filter || undefined, maxResults: 20 });
      setItems(res.campaigns);
      setNextToken(res.nextToken);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const res = await api.list({ status: filter || undefined, maxResults: 20, nextToken });
      setItems((p) => [...p, ...res.campaigns]);
      setNextToken(res.nextToken);
    } finally { setLoadingMore(false); }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.create({ name: newName, subject: newSubject,
        html_content: `<h1>${newSubject}</h1><p>Edit this content in the template editor.</p>` });
      setShowCreate(false); setNewName(""); setNewSubject("");
      load();
    } finally { setCreating(false); }
  };

  const handleSend = async (id: string) => {
    if (!confirm("Send this campaign to all active subscribers?")) return;
    setActionLoading(id);
    try {
      const res = await api.send(id);
      alert(res.message);
      load();
    } catch (e: any) { alert(e.message || "Send failed"); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this campaign? This cannot be undone.")) return;
    setActionLoading(id);
    try { await api.delete(id); load(); }
    catch (e: any) { alert(e.message || "Delete failed"); }
    finally { setActionLoading(null); }
  };

  const handleDuplicate = async (c: Campaign) => {
    try {
      await api.create({ name: `${c.name} (copy)`, subject: c.subject || "",
        html_content: c.html_content || "" });
      load();
    } catch (e: any) { alert(e.message || "Duplicate failed"); }
  };

  const handleResendNew = async (id: string) => {
    if (!confirm("Send this campaign to all new subscribers who haven't received it yet?")) return;
    setActionLoading(id);
    try {
      const res = await api.resendNew(id);
      alert(res.message);
      load();
    } catch (e: any) { alert(e.message || "Resend failed"); }
    finally { setActionLoading(null); }
  };

  const handlePause = async (id: string) => {
    setActionLoading(id);
    try {
      await api.pause(id);
      load();
    } catch (e: any) { alert(e.message || "Pause failed"); }
    finally { setActionLoading(null); }
  };

  const handleResume = async (id: string) => {
    setActionLoading(id);
    try {
      await api.resume(id);
      load();
    } catch (e: any) { alert(e.message || "Resume failed"); }
    finally { setActionLoading(null); }
  };

  const filtered = search
    ? items.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    : items;

  const inp = "w-full px-4 py-2.5 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50";

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-ink">Campaigns</h1>
            <p className="text-xs sm:text-sm text-slate mt-0.5">{items.length} campaign{items.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/campaigns/prebuilt"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-teal-light border border-teal/20 text-sm font-semibold text-teal hover:bg-teal/10 hover:border-teal/40 transition-colors">
              <Download size={14} /> Prebuilt
            </Link>
            <Link to="/campaigns/generate"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-coral/5 border border-coral/20 text-sm font-semibold text-coral hover:bg-coral/10 hover:border-coral/40 transition-colors">
              <Zap size={14} /> AI Generate
            </Link>
            <Link to="/campaigns/wizard"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/[0.14] hover:text-ink transition-colors">
              <Sparkles size={14} className="text-coral" /> AI Wizard
            </Link>
            <button onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/25">
              <Plus size={15} />
              <span className="hidden sm:inline">New Campaign</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5">
        {/* Search + filter bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search campaigns…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/[0.08] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-coral/30" />
          </div>
          <div className="flex gap-1.5 p-1 bg-black/[0.04] rounded-xl overflow-x-auto">
            {STATUS_FILTERS.map((f) => (
              <button key={f || "all"} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  filter === f ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink"
                }`}>
                {f || "All"}
              </button>
            ))}
          </div>
        </div>

        {loading ? <Spinner className="py-20" /> : (
          <>
            {/* Campaign grid */}
            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-black/[0.06] py-16 sm:py-20 text-center">
                <Mail size={32} className="text-slate opacity-20 mx-auto mb-4" />
                <p className="font-semibold text-ink mb-1">No campaigns yet</p>
                <p className="text-sm text-slate mb-6">Create your first campaign or use the AI wizard to get started in seconds.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/campaigns/wizard"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
                    <Sparkles size={15} /> Try AI Wizard
                  </Link>
                  <button onClick={() => setShowCreate(true)}
                    className="px-5 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold hover:border-black/[0.14]">
                    Create manually
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block bg-white rounded-2xl border border-black/[0.06]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-black/[0.06]">
                        {["Campaign", "Status", "Sent", "Open Rate", "Date", ""].map((h) => (
                          <th key={h} className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c) => {
                        const sent   = Number(c.sent_count || 0);
                        const opens  = Number(c.open_count || 0);
                        const isLoading = actionLoading === c.id;
                        return (
                          <tr key={c.id} className="border-b border-black/[0.04] hover:bg-black/[0.01] transition-colors">
                            <td className="px-5 py-4">
                              <Link to={`/campaigns/${c.id}`} className="font-semibold text-ink hover:text-coral truncate max-w-[200px] block transition-colors">{c.name}</Link>
                              {c.subject && <p className="text-xs text-slate truncate max-w-[200px] mt-0.5">{c.subject}</p>}
                              <button
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(c.id); const btn = e.currentTarget; btn.textContent = "Copied!"; setTimeout(() => { btn.textContent = c.id; }, 1500); }}
                                className="text-[10px] font-mono text-slate/40 hover:text-coral mt-1 truncate max-w-[200px] block text-left cursor-pointer transition-colors"
                                title="Click to copy campaign ID"
                              >{c.id}</button>
                            </td>
                            <td className="px-5 py-4">
                              <StatusBadge status={c.status} />
                              {!!c.auto_optimize && (
                                <span className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                  c.auto_optimize_status === "completed" ? "bg-green-50 text-green-600" :
                                  c.auto_optimize_status === "triggered" ? "bg-amber-50 text-amber-600" :
                                  "bg-blue-50 text-blue-600"
                                }`}>
                                  {c.auto_optimize_status === "completed" ? "optimized" :
                                   c.auto_optimize_status === "triggered" ? "optimizing" :
                                   c.auto_optimize_status === "skipped" ? "ok" : "auto"}
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-4 text-slate">{sent > 0 ? sent.toLocaleString() : "—"}</td>
                            <td className="px-5 py-4 font-semibold">
                              {sent > 0 ? `${((opens / sent) * 100).toFixed(1)}%` : "—"}
                            </td>
                            <td className="px-5 py-4 text-slate text-xs whitespace-nowrap">
                              {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-1 justify-end">
                                {/* Preview */}
                                <button onClick={() => setPreviewCampaign(c)} title="Inbox preview"
                                  className="p-2 rounded-lg text-slate hover:text-ink hover:bg-black/[0.04] transition-colors">
                                  <Eye size={15} />
                                </button>
                                {/* Optimize */}
                                {c.status === "sent" && (
                                  <button onClick={() => setOptimizeCampaign(c)} title="Optimize"
                                    className="p-2 rounded-lg text-slate hover:text-coral hover:bg-coral/[0.06] transition-colors">
                                    <TrendingUp size={15} />
                                  </button>
                                )}
                                {/* Send */}
                                {c.status === "draft" && (
                                  <button onClick={() => handleSend(c.id)} disabled={isLoading} title="Send"
                                    className="p-2 rounded-lg text-slate hover:text-teal hover:bg-teal/[0.06] transition-colors disabled:opacity-40">
                                    <Send size={15} />
                                  </button>
                                )}
                                {/* Pause */}
                                {(c.status === "sending" || c.status === "throttled" || c.status === "scheduled") && (
                                  <button onClick={() => handlePause(c.id)} disabled={isLoading} title="Pause"
                                    className="p-2 rounded-lg text-slate hover:text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-40">
                                    <Pause size={15} />
                                  </button>
                                )}
                                {/* Resume */}
                                {c.status === "paused" && (
                                  <button onClick={() => handleResume(c.id)} disabled={isLoading} title="Resume"
                                    className="p-2 rounded-lg text-slate hover:text-teal hover:bg-teal/[0.06] transition-colors disabled:opacity-40">
                                    <Play size={15} />
                                  </button>
                                )}
                                {/* More menu */}
                                <div className="relative">
                                  <button onClick={() => setMenuOpen(menuOpen === c.id ? null : c.id)}
                                    className="p-2 rounded-lg text-slate hover:text-ink hover:bg-black/[0.04] transition-colors">
                                    <MoreVertical size={15} />
                                  </button>
                                  {menuOpen === c.id && (
                                    <div className="absolute right-0 top-9 w-48 bg-white rounded-xl shadow-xl border border-black/[0.08] z-20 py-1">
                                      <button onClick={() => { handleDuplicate(c); setMenuOpen(null); }}
                                        className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-slate hover:text-ink hover:bg-black/[0.02]">
                                        <Copy size={13} /> Duplicate
                                      </button>
                                      {c.status !== "draft" && (
                                        <Link to={`/campaigns/${c.id}/send-log`} onClick={() => setMenuOpen(null)}
                                          className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-slate hover:text-ink hover:bg-black/[0.02]">
                                          <FileText size={13} /> Send Log
                                        </Link>
                                      )}
                                      {(c.status === "sent" || c.status === "throttled") && (
                                        <button onClick={() => { handleResendNew(c.id); setMenuOpen(null); }}
                                          className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-teal hover:bg-teal/[0.06]">
                                          <UserPlus size={13} /> Resend to new
                                        </button>
                                      )}
                                      <button onClick={() => { handleDelete(c.id); setMenuOpen(null); }}
                                        className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-red-500 hover:bg-red-50">
                                        <Trash2 size={13} /> Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile card list */}
                <div className="md:hidden space-y-3">
                  {filtered.map((c) => {
                    const sent  = Number(c.sent_count || 0);
                    const opens = Number(c.open_count || 0);
                    return (
                      <div key={c.id} className="bg-white rounded-2xl border border-black/[0.06] p-4">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            <Link to={`/campaigns/${c.id}`} className="font-semibold text-ink hover:text-coral truncate block transition-colors">{c.name}</Link>
                            {c.subject && <p className="text-xs text-slate mt-0.5 truncate">{c.subject}</p>}
                            <button
                              onClick={() => { navigator.clipboard.writeText(c.id); }}
                              className="text-[10px] font-mono text-slate/40 hover:text-coral mt-1 truncate block text-left"
                              title="Tap to copy ID"
                            >{c.id}</button>
                          </div>
                          <StatusBadge status={c.status} />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-4 text-xs text-slate">
                            <span>{sent > 0 ? `${sent.toLocaleString()} sent` : "Not sent"}</span>
                            {sent > 0 && <span className="font-semibold text-ink">{((opens/sent)*100).toFixed(1)}% opens</span>}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => setPreviewCampaign(c)}
                              className="p-2 rounded-lg text-slate hover:text-ink hover:bg-black/[0.04]">
                              <Eye size={15} />
                            </button>
                            {c.status === "draft" && (
                              <button onClick={() => handleSend(c.id)} disabled={actionLoading === c.id}
                                className="p-2 rounded-lg text-slate hover:text-teal hover:bg-teal/[0.06] disabled:opacity-40">
                                <Send size={15} />
                              </button>
                            )}
                            {(c.status === "sent" || c.status === "throttled") && (
                              <button onClick={() => handleResendNew(c.id)} disabled={actionLoading === c.id}
                                title="Resend to new subscribers"
                                className="p-2 rounded-lg text-teal hover:bg-teal/[0.06] disabled:opacity-40">
                                <UserPlus size={15} />
                              </button>
                            )}
                            <button onClick={() => handleDelete(c.id)}
                              className="p-2 rounded-lg text-red-400 hover:bg-red-50">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Load more */}
                {nextToken && (
                  <div className="flex justify-center">
                    <button onClick={loadMore} disabled={loadingMore}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/[0.14] hover:bg-white disabled:opacity-60">
                      <ChevronDown size={15} />
                      {loadingMore ? "Loading…" : "Load more"}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* AI Wizard banner */}
        <div className="bg-ink rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-coral/20 flex-shrink-0">
              <Sparkles size={20} className="text-coral" />
            </div>
            <div>
              <p className="font-bold text-white text-sm">AI Campaign Wizard</p>
              <p className="text-white/50 text-xs mt-0.5">Describe your goal — AI writes the subject, body, and CTA in seconds.</p>
            </div>
          </div>
          <Link to="/campaigns/wizard"
            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/30">
            <Zap size={14} /> Open wizard
          </Link>
        </div>

        {/* Prebuilt Library banner */}
        <div className="bg-gradient-to-r from-teal/10 to-teal-light rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border border-teal/15">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-teal/15 flex-shrink-0">
              <Download size={20} className="text-teal" />
            </div>
            <div>
              <p className="font-bold text-ink text-sm">Pre-Built Campaign Library</p>
              <p className="text-slate text-xs mt-0.5">20+ ready-to-send email sequences — pick one, customize, and launch.</p>
            </div>
          </div>
          <Link to="/campaigns/prebuilt"
            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal/90 transition-colors shadow-md shadow-teal/20">
            <Mail size={14} /> Browse library
          </Link>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="w-full sm:max-w-md bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl">
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-black/[0.06]">
              <h2 className="font-bold text-ink">New Campaign</h2>
              <button onClick={() => setShowCreate(false)} className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04]">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="px-5 sm:px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Campaign name</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} required placeholder="e.g. October newsletter" className={inp} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Subject line</label>
                <input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} required placeholder="e.g. This month's updates" className={inp} />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowCreate(false)}
                  className="flex-1 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold hover:border-black/[0.14]">Cancel</button>
                <button type="submit" disabled={creating}
                  className="flex-1 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20">
                  {creating ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Panels */}
      {previewCampaign  && <InboxPreviewPanel  campaign={previewCampaign}  onClose={() => setPreviewCampaign(null)}  />}
      {optimizeCampaign && <OptimizationPanel  campaign={optimizeCampaign} onClose={() => setOptimizeCampaign(null)} />}

      {/* Close menu on outside click */}
      {menuOpen && <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />}
    </div>
  );
}
